import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import pluralize from 'pluralize';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';

import { useMediaQuery } from 'react-responsive';

import { ChirpButton } from 'javascripts/components/chirp_button';
import { ChirpSelect } from 'javascripts/components/chirp_select';
import { FormatButtons } from 'javascripts/components/deals/format_buttons';

import { ReactComponent as SortIcon } from 'images/sort-icon.svg';
import { ReactComponent as FilterIcon } from 'images/filter-icon.svg';
import { ReactComponent as SearchIcon } from 'images/search-icon.svg';
import { ReactComponent as CloseIcon } from 'images/x-icon.svg';
import { ReactComponent as SelectIcon } from 'images/select-icon.svg';

import styles from 'stylesheets/components/sort_and_filter_bar';

export function SortAndFilterBar(props) {
  const [isSearching, setIsSearching] = useState(false);
  const searchBarRef = useRef();

  const isMobile = useMediaQuery({ maxWidth: breakpoints.sm });

  useEffect(() => {
    if (isSearching) {
      searchBarRef.current.focus();
    }
  }, [isSearching]);

  function renderCount() {
    return (
      <p className={styles.currentlyDisplayedCount}>
        <span className={styles.showingLabel}>Showing: </span>
        {`${props.showingCount > 1 ? '1-' : ''}${props.showingCount} of ${props.totalCount}`}
      </p>
    );
  }

  function renderCurrentFilters() {
    if (_.isEmpty(props.currentFilters)) {
      return null;
    }

    return (
      <div className={styles.currentFilters}>
        <p className={styles.filterLabel}>Filters:</p>
        {_.map(props.currentFilters, filter => (
          <div
            role='checkbox'
            aria-checked
            className={styles.filter}
            key={filter}
            onClick={() => props.onFilterRemove(filter)}
            onTouchEnd={() => props.onFilterRemove(filter)}
          >
            <span>{props.filterOptions[filter]}</span>
            <CloseIcon className={styles.close} />
          </div>
        ))}
      </div>
    );
  }

  function renderSortText() {
    const text = props.sortOptions.find(option => option.value === props.currentSort)?.label || 'Sort';
    return text === 'Default' ? 'Sort' : text;
  }

  function renderSort() {
    return (
      <ChirpSelect
        options={props.sortOptions}
        onSelect={props.onSortSelect}
        selectLabel='Sort'
        selectedValue={props.currentSort}
      >
        <div className={styles.action}>
          <SortIcon className={styles.actionIcon} alt='' />
          <p>{renderSortText()}</p>
        </div>
      </ChirpSelect>
    );
  }

  function renderSelect() {
    if (!props.showSelectOption) return null;

    return (
      <div
        className={styles.action}
        onClick={() => props.setIsSelecting(true)}
        role='button'
        aria-labelledby='select-button-label'
      >
        <SelectIcon className={styles.actionIcon} alt='' />
        <p id='select-button-label'>Select</p>
      </div>
    );
  }

  function renderFilter() {
    if (!props.showFilterOption) return null;

    return (
      <div
        className={styles.action}
        onClick={props.openFilterModal}
        onTouchEnd={props.openFilterModal}
        role='button'
        aria-labelledby='filter-button-label'
      >
        <FilterIcon className={styles.actionIcon} alt='' />
        <p id='filter-button-label'>Filter</p>
      </div>
    );
  }

  function renderSearch() {
    if (!props.showSearchOption) return null;

    return (
      <div
        className={styles.action}
        onClick={() => setIsSearching(true)}
        role='button'
        aria-labelledby='search-button-label'
      >
        <SearchIcon className={styles.actionIcon} alt='' />
        <p id='search-button-label'>Search</p>
      </div>
    );
  }

  function renderSelectingView() {
    const count = (
      <p className={styles.currentlyDisplayedCount}>
        {`${props.selectedBooksCount} ${pluralize('book', props.selectedBooksCount)} selected`}
      </p>
    );
    // We need to render the sort options below even though it's hidden to avoid errors with onClickOutside, which expects the ref to always be part of the DOM.
    return (
      <React.Fragment>
        <div className={styles.bar}>
          {!isMobile && count}
          <div className={styles.actionButtons}>
            {_.map(props.bulkActions, action => (
              <ChirpButton
                class={styles.actionButton}
                displayType='link'
                displayStyle='underline'
                fontSize={isMobile ? 'small' : 'medium'}
                label={action.label}
                onClick={action.action}
                key={action.label}
              />
            ))}
            <ChirpButton
              class={styles.actionButton}
              displayType='link'
              displayStyle='underline'
              fontSize={isMobile ? 'small' : 'medium'}
              label='Cancel'
              onClick={() => props.setIsSelecting(false)}
            />
          </div>
        </div>
        {isMobile && count}
      </React.Fragment>
    );
  }

  function renderSearchField() {
    function handleChange(e) {
      props.onSearchTermChange(e.target.value);
    }

    function onCancel() {
      setIsSearching(false);
      props.onSearchTermChange(null);
    }

    return (
      <div className={styles.bar}>
        <div className={styles.searchBar}>
          <SearchIcon className={styles.searchIcon} alt='' />
          <input
            type='text'
            placeholder='Search...'
            onChange={handleChange}
            value={props.currentSearchTerm}
            className={styles.searchInput}
            ref={searchBarRef}
          />
          <ChirpButton displayType='link' fontSize={isMobile ? 'small' : 'medium'} label='Cancel' onClick={onCancel} />
        </div>
      </div>
    );
  }

  function renderFormatButtons() {
    if (!props.showFormatOption) return null;

    return <FormatButtons format={props.currentFormat} hideBackground onFormatChange={props.onFormatChange} />;
  }

  function renderActionButtons() {
    if (!props.showFormatOption) {
      return (
        <React.Fragment>
          {renderSelect()}
          {renderSort()}
          {renderFilter()}
          {renderSearch()}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <div className={styles.actionGroup}>
          {renderSelect()}
          {renderSort()}
          {renderFilter()}
          {renderSearch()}
        </div>
        {renderFormatButtons()}
      </React.Fragment>
    );
  }

  function renderContent() {
    if (isSearching) {
      return renderSearchField();
    }
    if (props.isSelecting) {
      return renderSelectingView();
    }

    return (
      <React.Fragment>
        <div className={styles.bar}>
          {!isMobile && renderCount()}
          <div className={styles.actionButtons}>{renderActionButtons()}</div>
        </div>
        {isMobile && renderCount()}
      </React.Fragment>
    );
  }

  return (
    <div className={styles.sortAndFilterBar} data-testid='sort-and-filter-bar'>
      {renderContent()}
      {renderCurrentFilters()}
    </div>
  );
}

SortAndFilterBar.propTypes = {
  bulkActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      action: PropTypes.func,
    }),
  ),
  currentFilters: PropTypes.arrayOf(PropTypes.string),
  currentFormat: PropTypes.string,
  currentSearchTerm: PropTypes.string,
  currentSort: PropTypes.string,
  filterOptions: PropTypes.object,
  isSelecting: PropTypes.bool,
  onFilterRemove: PropTypes.func,
  onFormatChange: PropTypes.func,
  onSearchTermChange: PropTypes.func,
  onSortSelect: PropTypes.func,
  openFilterModal: PropTypes.func,
  selectedBooksCount: PropTypes.number,
  setIsSelecting: PropTypes.func,
  showFilterOption: PropTypes.bool,
  showFormatOption: PropTypes.bool,
  showSearchOption: PropTypes.bool,
  showSelectOption: PropTypes.bool,
  showingCount: PropTypes.number,
  sortOptions: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  totalCount: PropTypes.number,
};

SortAndFilterBar.defaultProps = {
  showFilterOption: true,
  showFormatOption: false,
  showSearchOption: true,
  showSelectOption: true,
};
